import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Radio,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventBusyRoundedIcon from '@mui/icons-material/EventBusyRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import { format } from 'date-fns';
import type { Appointment } from '@/components/appointments/AppointmentCard';
import InlineFeedback from '@/components/shared/InlineFeedback';
import { getClientDisplayFromAppointment } from '@/utils/appointments/schedule-dialog-utils';

export type CancelAppointmentScope = 'this_only' | 'this_and_following' | 'all';

export interface CancelAppointmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: Appointment | null;
  clientName?: string;
  isRecurring?: boolean;
  isModified?: boolean;
  isParent?: boolean;
  isCancelling?: boolean;
  error?: { title?: string; message: string } | null;
  onConfirm: (scope: CancelAppointmentScope) => void | Promise<void>;
}

function parseAppointmentSummary(appointment: Appointment, clientName?: string) {
  const raw = appointment as unknown as Record<string, unknown>;
  const title = String(raw.title || appointment.type || 'Session').trim() || 'Session';
  const startSrc = raw.startTime ?? raw.start_time ?? appointment.date;
  const start = startSrc instanceof Date ? startSrc : new Date(String(startSrc));
  const endSrc = raw.endTime ?? raw.end_time;
  const end = endSrc
    ? endSrc instanceof Date
      ? endSrc
      : new Date(String(endSrc))
    : new Date(start.getTime() + Math.max(1, Number(appointment.duration) || 60) * 60_000);

  const type = String(raw.appointmentType || raw.mode || appointment.mode || '').toLowerCase();
  const location = String(raw.location || '').toLowerCase();
  const modeLabel =
    type === 'offline' || type === 'in_person' || location.includes('person')
      ? 'In-person'
      : 'Online';

  const resolvedClient =
    clientName?.trim() || getClientDisplayFromAppointment(appointment).name;

  return {
    title,
    clientName: resolvedClient,
    start,
    end,
    dateLabel: format(start, 'EEE, MMM d, yyyy'),
    timeLabel: `${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`,
    modeLabel,
  };
}

const scopeOptions: Array<{
  value: CancelAppointmentScope;
  label: string;
  description: string;
}> = [
  {
    value: 'this_only',
    label: 'This session only',
    description: 'Cancel just this occurrence. Other sessions in the series stay scheduled.',
  },
  {
    value: 'this_and_following',
    label: 'This and future sessions',
    description: 'Cancel this session and every upcoming one in the series.',
  },
  {
    value: 'all',
    label: 'Entire series',
    description: 'Cancel every session in this recurring series.',
  },
];

export const CancelAppointmentDialog: React.FC<CancelAppointmentDialogProps> = ({
  open,
  onOpenChange,
  appointment,
  clientName,
  isRecurring = false,
  isModified = false,
  isParent = false,
  isCancelling = false,
  error = null,
  onConfirm,
}) => {
  const [selectedScope, setSelectedScope] = useState<CancelAppointmentScope>('this_only');

  useEffect(() => {
    if (open) {
      setSelectedScope('this_only');
    }
  }, [open, appointment?.id]);

  const summary = useMemo(
    () => (appointment ? parseAppointmentSummary(appointment, clientName) : null),
    [appointment, clientName],
  );

  const visibleScopeOptions = useMemo(() => {
    if (!isRecurring) return [];
    return scopeOptions.filter((option) => {
      if (option.value === 'this_only') return true;
      return !isModified || isParent;
    });
  }, [isRecurring, isModified, isParent]);

  const handleClose = () => {
    if (isCancelling) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100vw - 28px)', sm: 480 },
          maxWidth: 'calc(100vw - 28px)',
          borderRadius: '18px',
          overflow: 'hidden',
          m: 0,
          bgcolor: '#fff',
          boxShadow: '0 30px 70px rgba(16, 24, 40, 0.24)',
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.4 },
          py: 1.35,
          borderBottom: '1px solid #EEF2F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '1rem', sm: '1.05rem' },
              fontWeight: 700,
              color: '#1D2939',
              lineHeight: 1.2,
            }}
          >
            Cancel appointment
          </Typography>
          {summary ? (
            <Typography
              sx={{
                mt: 0.25,
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.78rem',
                color: '#667085',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {summary.clientName} · {summary.dateLabel}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isCancelling}
          aria-label="Close"
          sx={{
            width: 32,
            height: 32,
            color: '#98A2B3',
            '&:hover': { bgcolor: '#F8FAFC' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.4 }, py: { xs: 2, sm: 2.25 } }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              borderRadius: '999px',
              bgcolor: '#FEE4E2',
              border: '1px solid #FECDCA',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#D92D20',
            }}
          >
            <EventBusyRoundedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              mt: 1.25,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '1.05rem', sm: '1.15rem' },
              color: '#1D2939',
              lineHeight: 1.25,
            }}
          >
            Cancel this appointment?
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.88rem',
              lineHeight: 1.45,
              color: '#667085',
              maxWidth: 360,
              mx: 'auto',
            }}
          >
            This removes the session from your calendar and notifies the client. This action cannot be undone.
          </Typography>
        </Box>

        {summary ? (
          <Box
            sx={{
              mt: 2,
              p: 1.35,
              borderRadius: '14px',
              border: '1px solid #E4E7EC',
              bgcolor: '#F8FAFC',
              display: 'grid',
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.92rem',
                color: '#1D2939',
                lineHeight: 1.3,
              }}
            >
              {summary.title}
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0 }}>
                <PersonRoundedIcon sx={{ fontSize: 16, color: '#667085', flexShrink: 0 }} />
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.82rem',
                    color: '#475467',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {summary.clientName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0 }}>
                <CalendarTodayRoundedIcon sx={{ fontSize: 16, color: '#667085', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#475467' }}>
                  {summary.dateLabel}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, minWidth: 0 }}>
                <AccessTimeRoundedIcon sx={{ fontSize: 16, color: '#667085', flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: '#475467' }}>
                  {summary.timeLabel} · {summary.modeLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : null}

        {isRecurring && visibleScopeOptions.length > 0 ? (
          <Box sx={{ mt: 2, display: 'grid', gap: 0.85 }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '0.78rem',
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#667085',
              }}
            >
              Recurring series
            </Typography>
            {visibleScopeOptions.map((option) => {
              const isSelected = selectedScope === option.value;
              return (
                <Box
                  key={option.value}
                  onClick={() => setSelectedScope(option.value)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    p: 1.15,
                    borderRadius: '12px',
                    border: `1px solid ${isSelected ? '#B9E6FE' : '#E4E7EC'}`,
                    bgcolor: isSelected ? '#F0F9FF' : '#fff',
                    cursor: 'pointer',
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                  }}
                >
                  <Radio
                    checked={isSelected}
                    value={option.value}
                    size="small"
                    sx={{
                      p: 0.25,
                      mt: 0.1,
                      color: '#98A2B3',
                      '&.Mui-checked': { color: '#2FA8D5' },
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '0.84rem',
                        color: '#1D2939',
                      }}
                    >
                      {option.label}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.2,
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '0.78rem',
                        lineHeight: 1.4,
                        color: '#667085',
                      }}
                    >
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : null}

        {error ? (
          <Box sx={{ mt: 2 }}>
            <InlineFeedback variant="error" title={error.title} message={error.message} />
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          px: { xs: 2, sm: 2.4 },
          py: 1.35,
          borderTop: '1px solid #EEF2F6',
          bgcolor: '#fff',
          display: 'flex',
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: 'stretch',
          gap: 1,
        }}
      >
        <Button
          type="button"
          variant="outlined"
          disabled={isCancelling}
          onClick={handleClose}
          sx={{
            flex: { sm: 1 },
            minHeight: 46,
            borderRadius: '12px',
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '0.92rem',
            color: '#475467',
            borderColor: '#D0D5DD',
            '&:hover': {
              borderColor: '#BFC7D2',
              bgcolor: '#F8FAFC',
            },
          }}
        >
          Keep appointment
        </Button>
        <Button
          type="button"
          variant="contained"
          disableElevation
          disabled={isCancelling || !appointment}
          onClick={() => void onConfirm(isRecurring ? selectedScope : 'this_only')}
          sx={{
            flex: { sm: 1 },
            minHeight: 46,
            borderRadius: '12px',
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '0.92rem',
            bgcolor: '#D92D20',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#B42318',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              bgcolor: '#FECDCA',
              color: '#fff',
              opacity: 1,
            },
          }}
        >
          {isCancelling ? 'Cancelling…' : 'Cancel appointment'}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CancelAppointmentDialog;
